import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, TreePine, Leaf, Flower2 } from "lucide-react";
import { renderMarkdown } from "@/lib/markdown";

const fmt = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; } };

const setMeta = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
};

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${id}`).then((r) => {
      setPost(r.data);
      const desc = String(r.data.body || "").replace(/\n/g, " ").slice(0, 160);
      document.title = `${r.data.title} · PlantFlow`;
      setMeta("og:title", r.data.title);
      setMeta("og:description", desc);
      setMeta("og:type", "article");
      if (r.data.cover_image) setMeta("og:image", r.data.cover_image);
    }).catch(() => setNotFound(true));
  }, [id]);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-900"><ArrowLeft className="w-4 h-4" /> All posts</Link>
        {notFound && <p data-testid="blogpost-notfound" className="mt-10 text-stone-500">This post isn't available.</p>}
        {post && (
          <article data-testid="blogpost" className="mt-6">
            {post.cover_image && <img data-testid="blogpost-cover" src={post.cover_image} alt={post.title} className="w-full h-72 object-cover rounded-3xl" />}
            <div className="mt-6 flex items-center gap-3">
              <Badge className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">{post.category}</Badge>
              <span className="text-xs text-stone-400">{fmt(post.date)}</span>
            </div>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-emerald-950 leading-tight">{post.title}</h1>
            <div className="mt-6 prose prose-lg prose-emerald max-w-none text-stone-700" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }} />
            {(post.blooming_tree || post.blooming_shrub || post.seasonal_annuals) && (
              <section data-testid="almanac-section" className="mt-12 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-white p-7 sm:p-9">
                <span className="text-xs uppercase tracking-[0.25em] text-emerald-700">Regional Almanac</span>
                <h2 className="mt-2 font-serif text-3xl text-emerald-950">This month in your zone</h2>
                <p className="mt-1 text-sm text-stone-500">What's flowering and thriving around South Florida right now.</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {post.blooming_tree && (
                    <div data-testid="almanac-tree" className="rounded-2xl bg-white border border-emerald-100 p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100 text-emerald-700"><TreePine className="h-5 w-5" /></div>
                      <h3 className="mt-3 font-medium text-emerald-950">Blooming Trees</h3>
                      <p className="mt-1 text-sm text-stone-600 whitespace-pre-line">{post.blooming_tree}</p>
                    </div>
                  )}
                  {post.blooming_shrub && (
                    <div data-testid="almanac-shrub" className="rounded-2xl bg-white border border-emerald-100 p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100 text-emerald-700"><Leaf className="h-5 w-5" /></div>
                      <h3 className="mt-3 font-medium text-emerald-950">Blooming Shrubs</h3>
                      <p className="mt-1 text-sm text-stone-600 whitespace-pre-line">{post.blooming_shrub}</p>
                    </div>
                  )}
                  {post.seasonal_annuals && (
                    <div data-testid="almanac-annuals" className="rounded-2xl bg-white border border-emerald-100 p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100 text-emerald-700"><Flower2 className="h-5 w-5" /></div>
                      <h3 className="mt-3 font-medium text-emerald-950">Seasonal Annuals</h3>
                      <p className="mt-1 text-sm text-stone-600 whitespace-pre-line">{post.seasonal_annuals}</p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </article>
        )}
      </div>
    </div>
  );
}
